import { API_BASE_URL } from '@synergy/config';
import { Box, IconButton, Tooltip } from '@mui/material';
import { CommentNoteIcon, DownloadIcon } from '@synergy/theme-next';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { PoolTooltip } from '@synergy/components-next';
import { ReactElement, useEffect } from 'react';
import { getBonusSettlementSupplierTranslationId, getBonusSettlementTypeTranslationId } from '@synergy/models';
import { tokens } from '@synergy/theme-palo';
import { useNavigate, useSearchParams } from 'react-router-dom';
import urlJoin from 'url-join';

import { BonusSettlement } from '../../models/bonus-settlement';
import { ROUTES } from '../../../sidebar-menu/navigation-drawer/routes';
import { bonusSettlementsSelector } from '../store/bonus-settlements-overview-selector';
import { defaultSearchParamsSelector } from '../../../../store/default-search-params-selector';
import { fetchBonusSettlements, resetBonusSettlementsState } from '../store/bonus-settlements-overview-actions';
import { useAppSelector } from '../../../../store/store';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const shortDate = (value?: Date | string | null): ReactElement =>
    value ? (
        <Box sx={{ whiteSpace: 'nowrap' }}>
            <FormattedDate value={value} year="numeric" month="short" day="numeric" />
        </Box>
    ) : (
        <>–</>
    );

export const PaloBonusSettlementOverview = (): ReactElement => {
    const dispatch = useThunkDispatch();
    const navigate = useNavigate();
    const { bonusSettlements, requesting } = useAppSelector(bonusSettlementsSelector);
    const defaultSearchParams = useAppSelector(defaultSearchParamsSelector);
    const [searchParams] = useSearchParams(defaultSearchParams);

    useEffect(() => {
        dispatch(fetchBonusSettlements({ searchParams }));

        return () => {
            dispatch(resetBonusSettlementsState());
        };
    }, [dispatch, searchParams]);

    const columns: PaloColumn<BonusSettlement>[] = [
        {
            key: 'status',
            type: 'status',
            head: <FormattedMessage id="app.table.column.status" />,
            cell: settlement => (
                <PaloStatusPill
                    status={settlement.status}
                    label={<FormattedMessage id={getBonusSettlementSupplierTranslationId(settlement.status)} />}
                />
            ),
        },
        {
            key: 'type',
            type: 'classification',
            head: <FormattedMessage id="app.common.type" />,
            cell: settlement => <FormattedMessage id={getBonusSettlementTypeTranslationId(settlement.type)} />,
        },
        {
            key: 'year',
            type: 'count',
            head: <FormattedMessage id="app.common.year" />,
            cell: settlement => settlement.year,
        },
        {
            key: 'billingPeriod',
            type: 'name',
            head: <FormattedMessage id="app.table.column.billingPeriod" />,
            cell: settlement => (
                <Box sx={{ whiteSpace: 'nowrap' }}>
                    <FormattedDate
                        value={new Date(`${settlement.year}-01-01`)}
                        year="numeric"
                        month="short"
                        day="numeric"
                    />
                    {' – '}
                    <FormattedDate
                        value={new Date(`${settlement.year}-12-31`)}
                        year="numeric"
                        month="short"
                        day="numeric"
                    />
                </Box>
            ),
        },
        {
            key: 'invitedAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.invitedAt" />,
            cell: settlement => shortDate(settlement.invitedAt),
        },
        {
            key: 'submittedAt',
            type: 'date',
            head: <FormattedMessage id="app.table.column.submittedAt" />,
            cell: settlement => shortDate(settlement.submittedAt),
        },
        {
            key: 'noteFromSupplier',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.noteFromSupplier" />,
            cell: settlement => (
                <PoolTooltip title={settlement.noteFromSupplier} placement="top">
                    <CommentNoteIcon
                        sx={{
                            fontSize: 16,
                            color: settlement.noteFromSupplier ? tokens.color.ink2 : tokens.color.ink3,
                        }}
                    />
                </PoolTooltip>
            ),
        },
        {
            key: 'download',
            type: 'icon',
            head: '',
            cell: settlement => (
                <Tooltip title={<FormattedMessage id="app.bonusSettlements.table.download" />} placement="top">
                    <IconButton
                        size="small"
                        onClick={event => {
                            // The row opens the settlement; this only downloads.
                            event.stopPropagation();
                            window.open(
                                urlJoin(API_BASE_URL, `/supplier-bonus-settlements/${settlement.id}/export`),
                                '_self'
                            );
                        }}
                    >
                        <DownloadIcon sx={{ fontSize: 17 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    return (
        <PaloListView<BonusSettlement>
            title={<FormattedMessage id="app.navigation.bonusSettlements" />}
            columns={columns}
            items={bonusSettlements.items}
            getRowId={settlement => settlement.id}
            requesting={requesting}
            emptyLabel={<FormattedMessage id="app.common.emptyResult" />}
            onRowClick={settlement => navigate(ROUTES.bonusSettlements.detail.replace(':id', settlement.id.toString()))}
        />
    );
};
